import type {
  ConversationLoggingRow,
  ConversationResponseParams,
} from "@mirage/service-conversation/service/conversationsLogger/types";
import { grpc } from "@mirage/service-dbx-api";
import { getConversationResponseRequest } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/context_observables";
import { getConversationResponse } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/conversations";
import {
  ConversationsAssistantResponse,
  ConversationsUserRequest,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/context_engine_conversations_service_pb";
import type { ConversationsResultSuccess } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import { tagged } from "@mirage/service-logging";

const logger = tagged("ConversationResponseLogger");

/*
 * Wraps the getConversationResponse function to log the params and response (if enabled).
 * @param addLog - Callback to add a log row to storage. undefined if logging is disabled.
 */
export function createLoggedConversationResponse(
  addLog: undefined | ((row: string) => void),
) {
  if (!addLog) {
    // If logging is disabled, do not touch the getConversationResponse function
    return {
      getResponse: grpc.getConversationResponse,
      logCompletion: () => {},
    };
  }

  let params: ConversationResponseParams | undefined;
  let request: ConversationsUserRequest | undefined;
  return {
    getResponse: (...args: Parameters<typeof grpc.getConversationResponse>) => {
      params = {
        userMessage: args[0],
        serverConversationId: args[1],
        history: args[2],
        experimentSetting: args[3],
        source: args[4],
        modelChoice: args[5],
        agentPayload: args[6],
        attachments: args[7],
        agentIds: args[8],
        rephrased: args[9],
        clientContext: args[10],
        searchParams: args[11],
        skipPersistence: args[12],
      };
      request = getConversationResponseRequest(...args);
      return getConversationResponse(...args);
    },
    logCompletion: (fullResult: ConversationsResultSuccess) => {
      if (!params) {
        throw new Error("No params set");
      }
      if (!request) {
        throw new Error("No raw request set");
      }
      const { unprocessedResponse, ...result } = fullResult;
      if (!unprocessedResponse) {
        throw new Error("No unprocessed response");
      }
      const sample: ConversationLoggingRow = {
        params,
        result,
        request,
        rawRequest: rawRequestToBase64(request),
        response: unprocessedResponse,
      };
      const sampleString = rowToString(sample);
      logger.log("getConversationResponse completion", sampleString);
      addLog(sampleString);
    },
  };
}

export function rawRequestToBase64(rawRequest: ConversationsUserRequest) {
  const binary = encodeEnvelopes({
    data: rawRequest.toBinary(),
    flags: 0,
  });
  const binaryString = String.fromCharCode(...binary);
  return btoa(binaryString);
}

export function rowToString(row: ConversationLoggingRow) {
  const dict = {
    params: row.params,
    result: row.result,
    request: row.request.toJson(),
    response: row.response.toJson(),
    rawRequest: row.rawRequest,
  };
  return JSON.stringify(
    dict,
    (_, value) => (typeof value === "bigint" ? Number(value) : value),
    2 /* indent */,
  );
}

export function parseRow(row: string): ConversationLoggingRow {
  const dict = JSON.parse(row);
  return {
    params: dict.params,
    result: dict.result,
    request: ConversationsUserRequest.fromJson(dict.request),
    response: ConversationsAssistantResponse.fromJson(dict.response),
    rawRequest: dict.rawRequest,
  };
}

// Copied from @bufbuild/connect-web/dist/types/envelope" to let us log the full protobuf request body

export function encodeEnvelopes(
  ...envelopes: { data: Uint8Array; flags: number }[]
) {
  const target = new ArrayBuffer(
    envelopes.reduce(
      (previousValue, currentValue) =>
        previousValue + currentValue.data.length + 5,
      0,
    ),
  );
  let offset = 0;
  for (const m of envelopes) {
    offset += encodeEnvelope(m, target, offset);
  }
  return new Uint8Array(target);
}

function encodeEnvelope(
  envelope: { data: Uint8Array; flags: number },
  target: ArrayBuffer,
  byteOffset: number,
) {
  const len = envelope.data.length + 5;
  const bytes = new Uint8Array(target, byteOffset, len);
  bytes[0] = envelope.flags; // first byte is flags
  for (let l = envelope.data.length, i = 4; i > 0; i--) {
    bytes[i] = l % 256; // 4 bytes message length
    l >>>= 8;
  }
  bytes.set(envelope.data, 5);
  return len;
}
